import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	FolderPlus,
	Plus,
	CheckCircle,
	Clock,
	Scale,
	User,
	Database,
	Code,
} from "lucide-react";
import type { Code as CodeAgent } from "@/lib/codes";
import type { Judge } from "@/lib/judges";
import type { Database as DatabaseType } from "@/integrations/supabase/types";

type Dataset = DatabaseType["public"]["Tables"]["test_data_sets"]["Row"];

interface Project {
	id: string;
	title: string;
	created_at: string;
	updated_at: string;
	user_id: string;
}

interface HomeViewProps {
	projects: Project[];
	agents: CodeAgent[];
	judges: Judge[];
	datasets: Dataset[];
	onSelectProject: (id: string) => void;
	onSelectAgent: (id: string) => void;
	onSelectJudge: (id: string) => void;
	onSelectDataset: (id: string) => void;
	onNewProject: () => void;
	onNewAgent: () => void;
	onNewJudge: () => void;
	onNewDataset: () => void;
	username?: string | undefined;
	user?: { id: string } | null;
}

export const HomeView = ({
	projects,
	agents,
	judges,
	datasets,
	onSelectProject,
	onSelectAgent,
	onSelectJudge,
	onSelectDataset,
	onNewProject,
	onNewAgent,
	onNewJudge,
	onNewDataset,
	username,
	user,
}: HomeViewProps) => {
	const recentProjects = projects.slice(0, 6);
	const recentAgents = agents.slice(0, 6);
	const recentJudges = judges.slice(0, 6);
	const recentDatasets = datasets.slice(0, 6);

	return (
		<div className="p-8 w-full">
			<div className="space-y-8">
				{/* Welcome Header */}
				<div className="text-center space-y-4">
					<h1 className="text-4xl font-bold text-foreground">
						Welcome back{username ? `, ${username}` : ""}!
					</h1>
					<p className="text-lg text-muted-foreground">Deepscan LM Dashboard</p>
				</div>

				{/* Quick Stats */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Total Evals</CardTitle>
							<FolderPlus className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">{projects.length}</div>
							<p className="text-xs text-muted-foreground">
								{projects.length === 0
									? "Create your first eval"
									: "Active evals"}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Total Agents
							</CardTitle>
							<Code className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">{agents.length}</div>
							<p className="text-xs text-muted-foreground">
								{agents.length === 0
									? "Create your first agent"
									: "JavaScript agents ready"}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Total Judges
							</CardTitle>
							<Scale className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">{judges.length}</div>
							<p className="text-xs text-muted-foreground">
								{judges.length === 0
									? "Create your first judge"
									: "AI evaluators ready"}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Total Datasets
							</CardTitle>
							<Database className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">{datasets.length}</div>
							<p className="text-xs text-muted-foreground">
								{datasets.length === 0
									? "Create your first dataset"
									: "Test datasets ready"}
							</p>
						</CardContent>
					</Card>
				</div>

				{/* Recent Evals Section */}
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h2 className="text-2xl font-semibold text-foreground">
							Recent Evals
						</h2>
						<Button onClick={onNewProject} className="flex items-center gap-2">
							<Plus className="h-4 w-4" />
							New Eval
						</Button>
					</div>

					{recentProjects.length === 0 ? (
						<Card className="border-dashed">
							<CardContent className="flex flex-col items-center justify-center py-12">
								<FolderPlus className="h-12 w-12 text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold text-foreground mb-2">
									No evals yet
								</h3>
								<p className="text-muted-foreground mb-4 text-center">
									Create your first eval to get started with testing and
									evaluation
								</p>
								<Button onClick={onNewProject}>
									<Plus className="h-4 w-4 mr-2" />
									Create Eval
								</Button>
							</CardContent>
						</Card>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{recentProjects.map((project) => (
								<Card
									key={project.id}
									className="cursor-pointer hover:shadow-md transition-shadow"
								>
									<CardHeader>
										<div className="flex items-center justify-between">
											<FolderPlus className="h-5 w-5 text-muted-foreground" />
											<div className="flex items-center text-xs text-muted-foreground">
												<Clock className="h-3 w-3 mr-1" />
												{formatDistanceToNow(new Date(project.updated_at), {
													addSuffix: true,
												})}
											</div>
										</div>
										<CardTitle className="text-lg">{project.title}</CardTitle>
										<CardDescription>
											Created{" "}
											{formatDistanceToNow(new Date(project.created_at), {
												addSuffix: true,
											})}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<Button
											variant="outline"
											className="w-full"
											onClick={() => onSelectProject(project.id)}
										>
											Open Eval
										</Button>
									</CardContent>
								</Card>
							))}
						</div>
					)}
				</div>

				{/* Recent Datasets Section */}
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h2 className="text-2xl font-semibold text-foreground">
							Recent Datasets
						</h2>
						<Button onClick={onNewDataset} className="flex items-center gap-2">
							<Plus className="h-4 w-4" />
							New Dataset
						</Button>
					</div>

					{recentDatasets.length === 0 ? (
						<Card className="border-dashed">
							<CardContent className="flex flex-col items-center justify-center py-12">
								<Database className="h-12 w-12 text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold text-foreground mb-2">
									No datasets yet
								</h3>
								<p className="text-muted-foreground mb-4 text-center">
									Create your first test dataset to start evaluating your agents
								</p>
								<Button onClick={onNewDataset}>
									<Plus className="h-4 w-4 mr-2" />
									Create Dataset
								</Button>
							</CardContent>
						</Card>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{recentDatasets.map((dataset) => (
								<Card
									key={dataset.id}
									className="cursor-pointer hover:shadow-md transition-shadow"
								>
									<CardHeader>
										<div className="flex items-center justify-between mb-2">
											<div className="flex items-center gap-2">
												<Database className="h-5 w-5 text-muted-foreground" />
												<Badge
													variant={
														dataset.type === "primary" ? "default" : "secondary"
													}
												>
													{dataset.type}
												</Badge>
												{dataset.is_public && (
													<Badge variant="outline">Public</Badge>
												)}
											</div>
											<div className="flex items-center text-xs text-muted-foreground">
												<Clock className="h-3 w-3 mr-1" />
												{formatDistanceToNow(new Date(dataset.updated_at), {
													addSuffix: true,
												})}
											</div>
										</div>
										<CardTitle className="text-lg">{dataset.name}</CardTitle>
										<div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
											<User className="h-3 w-3" />
											<span>
												{dataset.user_id === user?.id ? "You" : "Shared"}
											</span>
										</div>
										<CardDescription>
											Created{" "}
											{formatDistanceToNow(new Date(dataset.created_at), {
												addSuffix: true,
											})}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<Button
											variant="outline"
											className="w-full"
											onClick={() => onSelectDataset(dataset.id)}
										>
											View Dataset
										</Button>
									</CardContent>
								</Card>
							))}
						</div>
					)}
				</div>

				{/* Recent Agents Section */}
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h2 className="text-2xl font-semibold text-foreground">
							Recent Agents
						</h2>
						<Button onClick={onNewAgent} className="flex items-center gap-2">
							<Plus className="h-4 w-4" />
							New Agent
						</Button>
					</div>

					{recentAgents.length === 0 ? (
						<Card className="border-dashed">
							<CardContent className="flex flex-col items-center justify-center py-12">
								<Code className="h-12 w-12 text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold text-foreground mb-2">
									No agents yet
								</h3>
								<p className="text-muted-foreground mb-4 text-center">
									Create your first agent to start running JavaScript logic
								</p>
								<Button onClick={onNewAgent} variant="outline">
									<Plus className="h-4 w-4 mr-2" />
									Create Agent
								</Button>
							</CardContent>
						</Card>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{recentAgents.map((agent) => (
								<Card
									key={agent.id}
									className="cursor-pointer hover:shadow-md transition-shadow"
								>
									<CardHeader>
										<div className="flex items-center justify-between mb-2">
											<div className="flex items-center gap-2">
												<Code className="h-5 w-5 text-muted-foreground" />
												<Badge
													variant="default"
													className="flex items-center gap-1"
												>
													<CheckCircle className="h-2 w-2" />
													Ready
												</Badge>
											</div>
											<div className="flex items-center text-xs text-muted-foreground">
												<Clock className="h-3 w-3 mr-1" />
												{formatDistanceToNow(new Date(agent.updated_at), {
													addSuffix: true,
												})}
											</div>
										</div>
										<CardTitle className="text-lg">{agent.title}</CardTitle>
										<div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
											<User className="h-3 w-3" />
											<span>
												{agent.user_id === user?.id
													? "You"
													: agent.owner_email ?? "Unknown"}
											</span>
										</div>
										<CardDescription>
											Runtime: JavaScript • Created{" "}
											{formatDistanceToNow(new Date(agent.created_at), {
												addSuffix: true,
											})}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<Button
											variant="outline"
											className="w-full"
											onClick={() => onSelectAgent(agent.id)}
										>
											Configure Agent
										</Button>
									</CardContent>
								</Card>
							))}
						</div>
					)}
				</div>

				{/* Recent Judges Section */}
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h2 className="text-2xl font-semibold text-foreground">
							Recent Judges
						</h2>
						<Button onClick={onNewJudge} className="flex items-center gap-2">
							<Plus className="h-4 w-4" />
							New Judge
						</Button>
					</div>

					{recentJudges.length === 0 ? (
						<Card className="border-dashed">
							<CardContent className="flex flex-col items-center justify-center py-12">
								<Scale className="h-12 w-12 text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold text-foreground mb-2">
									No judges yet
								</h3>
								<p className="text-muted-foreground mb-4 text-center">
									Create your first AI judge to start evaluating agent outputs
								</p>
								<Button onClick={onNewJudge} variant="outline">
									<Plus className="h-4 w-4 mr-2" />
									Create Judge
								</Button>
							</CardContent>
						</Card>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{recentJudges.map((judge) => (
								<Card
									key={judge.id}
									className="cursor-pointer hover:shadow-md transition-shadow"
								>
									<CardHeader>
										<div className="flex items-center justify-between mb-2">
											<div className="flex items-center gap-2">
												<Scale className="h-5 w-5 text-muted-foreground" />
												<Badge
													variant="default"
													className="flex items-center gap-1"
												>
													<CheckCircle className="h-2 w-2" />
													Ready
												</Badge>
											</div>
											<div className="flex items-center text-xs text-muted-foreground">
												<Clock className="h-3 w-3 mr-1" />
												{formatDistanceToNow(new Date(judge.updated_at), {
													addSuffix: true,
												})}
											</div>
										</div>
										<CardTitle className="text-lg">{judge.title}</CardTitle>
										<div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
											<User className="h-3 w-3" />
											<span>
												{judge.user_id === user?.id ? "You" : judge.owner_email}
											</span>
										</div>
										<CardDescription>
											Model: {judge.model} • Created{" "}
											{formatDistanceToNow(new Date(judge.created_at), {
												addSuffix: true,
											})}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<Button
											variant="outline"
											className="w-full"
											onClick={() => onSelectJudge(judge.id)}
										>
											Configure Judge
										</Button>
									</CardContent>
								</Card>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
